import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';

export const ThemeContext = React.createContext({
    theme: 'light',
    mode: 'auto',
    isDark: false,
    isForced: false,
    period: 'day',
    setTheme: () => {},
    toggleTheme: () => {},
    clearForce: () => {},
    setThemeOverride: () => {},
});

const THEME_MODE_COOKIE = 'sitetheme_mode';
const THEME_FORCED_PERIOD_COOKIE = 'sitetheme_forced_period';

/** Light from 06:00 inclusive to 18:00 exclusive (local time) */
export const getDayPeriod = (date = new Date()) => {
    const hour = date.getHours();
    return hour >= 6 && hour < 18 ? 'day' : 'night';
};

export const themeFromPeriod = (period) => (period === 'night' ? 'dark' : 'light');

const applyThemeToDocument = () => {
    document.documentElement.setAttribute('data-theme', 'light');
    document.documentElement.style.colorScheme = 'light';
    if (document.body) {
        document.body.setAttribute('data-theme', 'light');
    }
};

const readCookies = () => {
    try {
        const cookies = new Cookies();
        const modeRaw = cookies.get(THEME_MODE_COOKIE);
        const mode = modeRaw === 'light' || modeRaw === 'dark' || modeRaw === 'auto' ? modeRaw : 'auto';
        const forcedPeriodRaw = cookies.get(THEME_FORCED_PERIOD_COOKIE);
        const forcedPeriod = forcedPeriodRaw === 'day' || forcedPeriodRaw === 'night' ? forcedPeriodRaw : null;
        return { mode, forcedPeriod };
    } catch (e) {
        return { mode: 'auto', forcedPeriod: null };
    }
};

const writeCookies = (mode, forcedPeriod) => {
    try {
        const cookies = new Cookies();
        cookies.set(THEME_MODE_COOKIE, mode, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        if (mode === 'auto' || !forcedPeriod) {
            cookies.remove(THEME_FORCED_PERIOD_COOKIE, { path: '/' });
        } else {
            cookies.set(THEME_FORCED_PERIOD_COOKIE, forcedPeriod, { path: '/', maxAge: 60 * 60 * 24 * 365 });
        }
    } catch (e) {}
};

/** If user forced a theme, keep it only until day/night period changes — then reset to auto */
export const resolveThemeState = (mode, forcedPeriod, now = new Date()) => {
    const period = getDayPeriod(now);
    let nextMode = mode === 'light' || mode === 'dark' || mode === 'auto' ? mode : 'auto';
    let nextForcedPeriod = forcedPeriod;

    if (nextMode !== 'auto') {
        if (!nextForcedPeriod) {
            nextForcedPeriod = period;
        } else if (nextForcedPeriod !== period) {
            nextMode = 'auto';
            nextForcedPeriod = null;
        }
    } else {
        nextForcedPeriod = null;
    }

    const theme = nextMode === 'auto' ? themeFromPeriod(period) : nextMode;
    return { mode: nextMode, forcedPeriod: nextForcedPeriod, period, theme };
};

const msUntilNextPeriodChange = (now = new Date()) => {
    const next = new Date(now);
    const hour = now.getHours();
    if (hour >= 6 && hour < 18) {
        next.setHours(18, 0, 0, 0);
    } else if (hour < 6) {
        next.setHours(6, 0, 0, 0);
    } else {
        next.setDate(next.getDate() + 1);
        next.setHours(6, 0, 0, 0);
    }
    return Math.max(1500, next.getTime() - now.getTime() + 50);
};

export function ThemeProvider({ children }) {
    applyThemeToDocument();

    const boot = useMemo(() => {
        const saved = readCookies();
        return resolveThemeState(saved.mode, saved.forcedPeriod);
    }, []);

    const [mode, setMode] = useState(boot.mode);
    const [forcedPeriod, setForcedPeriod] = useState(boot.forcedPeriod);
    const [period, setPeriod] = useState(boot.period);
    const [theme, setThemeState] = useState(boot.theme);
    const [themeOverride, setThemeOverrideState] = useState(null);

    const effectiveTheme = 'light';

    const commit = useCallback((nextMode, nextForcedPeriod) => {
        const resolved = resolveThemeState(nextMode, nextForcedPeriod);
        setMode(resolved.mode);
        setForcedPeriod(resolved.forcedPeriod);
        setPeriod(resolved.period);
        setThemeState(resolved.theme);
        if (!(themeOverride === 'light' || themeOverride === 'dark')) {
            applyThemeToDocument();
        }
        writeCookies(resolved.mode, resolved.forcedPeriod);
        return resolved;
    }, [themeOverride]);

    useEffect(() => {
        applyThemeToDocument();
        writeCookies('light', null);
    }, [effectiveTheme, mode, forcedPeriod]);

    useEffect(() => {
        let timeoutId;
        let intervalId;

        const refreshFromSchedule = () => {
            const saved = readCookies();
            commit(saved.mode, saved.forcedPeriod);
        };

        const scheduleBoundary = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                refreshFromSchedule();
                scheduleBoundary();
            }, msUntilNextPeriodChange());
        };

        scheduleBoundary();
        intervalId = setInterval(refreshFromSchedule, 60 * 1000);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') refreshFromSchedule();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [commit]);

    const setTheme = useCallback(
        (next) => {
            const forced = next === 'dark' ? 'dark' : 'light';
            commit(forced, getDayPeriod());
        },
        [commit],
    );

    const clearForce = useCallback(() => {
        commit('auto', null);
    }, [commit]);

    const toggleTheme = useCallback(() => {
        const periodNow = getDayPeriod();
        const autoTheme = themeFromPeriod(periodNow);
        if (mode === 'auto') {
            // Force the opposite of the automatic theme until the next day/night change
            const forced = autoTheme === 'dark' ? 'light' : 'dark';
            commit(forced, periodNow);
        } else {
            // Return to automatic schedule
            commit('auto', null);
        }
    }, [commit, mode]);

    const setThemeOverride = useCallback((next) => {
        setThemeOverrideState(next === 'light' || next === 'dark' ? next : null);
    }, []);

    const value = useMemo(
        () => ({
            theme: 'light',
            mode: 'light',
            isDark: false,
            isForced: true,
            period,
            setTheme,
            toggleTheme,
            clearForce,
            setThemeOverride,
        }),
        [period, setTheme, toggleTheme, clearForce, setThemeOverride],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
